import React from 'react';
import type { OfferLeistungszeitraum } from '../../../types';
import { AppSelectDropdown } from '../../../components/AppSelectDropdown';
import { DatePicker } from '../../../components/DatePicker';
import { APP_BTN_OUTLINE_CLASS, APP_BTN_PRIMARY_CLASS } from '../../../ui/controlStyles';
import { UI_Z_INDEX } from '../../../utils/uiZIndex';
import {
  formatLeistungszeitraumDisplay,
  isoWeekCount,
  isoWeekFromYmd,
  isoWeekNow,
  isoWeekToDateRange,
  isOfferWeekLeistungszeitraum,
  parseOfferLeistungszeitraum,
} from '../utils/offerLeistungszeitraum';
import { OfferFloatingEditShell } from './OfferFloatingEditShell';
import { useOfferDocumentIssued } from './offerDocumentIssuedContext';

export type OfferLeistungszeitraumEditProps = {
  value: OfferLeistungszeitraum | null;
  onSave: (next: OfferLeistungszeitraum | null) => void | Promise<void>;
  viewTextClassName?: string;
  viewWrapperClassName?: string;
  emptyHint?: string;
  /** Invoices: reset to issue date instead of clearing. Work reports keep delete. */
  clearMode?: 'delete' | 'reset';
  resetValue?: OfferLeistungszeitraum | null;
  /** Wenn gesetzt: „Ausblenden“ im Popup (z. B. Angebot, Abschlagsrechnung). */
  onHide?: () => void | Promise<void>;
  'aria-label'?: string;
};

type EditMode = 'day' | 'range' | 'week';

const SELECT_TRIGGER = '!min-h-0 h-9 !py-0';

function yearOptions(extra: number[]): Array<{ value: string; label: string }> {
  const nowYear = isoWeekNow().year;
  const years = new Set<number>();
  for (let y = nowYear - 3; y <= nowYear + 3; y += 1) years.add(y);
  for (const y of extra) {
    if (Number.isInteger(y) && y >= 2000 && y <= 2100) years.add(y);
  }
  return [...years]
    .sort((a, b) => a - b)
    .map((y) => ({ value: String(y), label: String(y) }));
}

function weekOptions(year: number): Array<{ value: string; label: string }> {
  const count = isoWeekCount(year);
  return Array.from({ length: count }, (_, i) => {
    const week = i + 1;
    return { value: String(week), label: `KW ${week}` };
  });
}

function clampWeek(year: number, week: number): number {
  const max = isoWeekCount(year);
  return Math.min(Math.max(1, week), max);
}

const modeBtn = (active: boolean) =>
  `inline-flex h-9 items-center rounded-md px-3 text-sm font-medium transition-colors ${
    active
      ? 'bg-pipedrive-blue text-white'
      : 'border border-app-border bg-app-surface text-app-text-muted hover:bg-app-hover hover:text-app-text'
  }`;

export const OfferLeistungszeitraumEdit: React.FC<OfferLeistungszeitraumEditProps> = ({
  value,
  onSave,
  viewTextClassName = '',
  viewWrapperClassName = '',
  emptyHint = '—',
  clearMode = 'delete',
  resetValue = null,
  onHide,
  'aria-label': ariaLabel = 'Leistungszeitraum',
}) => {
  const [editing, setEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [mode, setMode] = React.useState<EditMode>('day');
  const [from, setFrom] = React.useState('');
  const [to, setTo] = React.useState('');
  const [year, setYear] = React.useState(() => isoWeekNow().year);
  const [week, setWeek] = React.useState(() => isoWeekNow().week);
  const [toYear, setToYear] = React.useState(() => isoWeekNow().year);
  const [toWeek, setToWeek] = React.useState(() => isoWeekNow().week);
  const [weekRange, setWeekRange] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);

  const issued = useOfferDocumentIssued();
  const display = formatLeistungszeitraumDisplay(value);
  const isEmptyDisplay = display === '—';

  const applyParsed = (parsed: OfferLeistungszeitraum | null) => {
    if (parsed && isOfferWeekLeistungszeitraum(parsed)) {
      const endYear = parsed.toYear ?? parsed.year;
      const endWeek = parsed.toWeek ?? parsed.week;
      setMode('week');
      setYear(parsed.year);
      setWeek(parsed.week);
      setToYear(endYear);
      setToWeek(endWeek);
      setWeekRange(endYear !== parsed.year || endWeek !== parsed.week);
      setFrom('');
      setTo('');
      return;
    }
    if (parsed && !isOfferWeekLeistungszeitraum(parsed)) {
      setFrom(parsed.from);
      setTo(parsed.to && parsed.to !== parsed.from ? parsed.to : '');
      setMode(parsed.to && parsed.to !== parsed.from ? 'range' : 'day');
      return;
    }
    setFrom('');
    setTo('');
    setMode('day');
    setWeekRange(false);
  };

  if (issued) {
    return (
      <span
        className={`whitespace-pre-wrap break-words block tabular-nums ${isEmptyDisplay ? 'text-neutral-400' : ''} ${viewTextClassName} ${viewWrapperClassName}`}
      >
        {isEmptyDisplay ? '—' : display}
      </span>
    );
  }

  const openEdit = () => {
    applyParsed(parseOfferLeistungszeitraum(value));
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
  };

  const switchToWeek = () => {
    const snap = isoWeekFromYmd(from) ?? isoWeekFromYmd(to) ?? isoWeekNow();
    setYear(snap.year);
    setWeek(clampWeek(snap.year, snap.week));
    if (mode === 'range' && to) {
      const end = isoWeekFromYmd(to) ?? snap;
      setToYear(end.year);
      setToWeek(clampWeek(end.year, end.week));
      setWeekRange(end.year !== snap.year || end.week !== snap.week);
    } else {
      setToYear(snap.year);
      setToWeek(clampWeek(snap.year, snap.week));
      setWeekRange(false);
    }
    setMode('week');
  };

  const switchToDates = (nextMode: 'day' | 'range') => {
    const start = isoWeekToDateRange(year, week);
    const end = isoWeekToDateRange(toYear, toWeek);
    if (mode === 'week' && start) {
      setFrom(start.from);
      if (nextMode === 'range' && end) {
        setTo(end.to);
      } else {
        setTo('');
      }
    } else if (nextMode === 'range' && from && !to) {
      setTo(from);
    }
    setMode(nextMode);
  };

  const save = async () => {
    let next: OfferLeistungszeitraum | null = null;
    if (mode === 'week') {
      const draft: OfferLeistungszeitraum = weekRange
        ? { kind: 'week', year, week, toYear, toWeek }
        : { kind: 'week', year, week };
      next = parseOfferLeistungszeitraum(draft);
      if (!next) {
        window.alert('Bitte eine gültige Kalenderwoche wählen. Die End-KW muss nach der Start-KW liegen.');
        return;
      }
    } else if (from.trim()) {
      const fromYmd = from.trim().slice(0, 10);
      const toYmd = mode === 'range' && to.trim() ? to.trim().slice(0, 10) : '';
      const draft: OfferLeistungszeitraum =
        toYmd && toYmd !== fromYmd ? { from: fromYmd, to: toYmd } : { from: fromYmd };
      next = parseOfferLeistungszeitraum(draft);
      if (!next) {
        window.alert(
          mode === 'range'
            ? 'Das Enddatum muss am oder nach dem Startdatum liegen.'
            : 'Bitte gültiges Datum (TT.MM.JJJJ) eingeben.',
        );
        return;
      }
    } else if (clearMode === 'reset') {
      next = parseOfferLeistungszeitraum(resetValue);
      if (!next) {
        window.alert('Bitte gültiges Datum (TT.MM.JJJJ) eingeben.');
        return;
      }
    }
    setSaving(true);
    try {
      await Promise.resolve(onSave(next));
      setEditing(false);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      // Offene Jahr-/KW-Auswahl schließt zuerst nur sich selbst.
      if (document.querySelector('[data-app-select-list]')) return;
      e.preventDefault();
      cancel();
    }
  };

  const fromYearOpts = yearOptions([year, toYear]);
  const toYearOpts = yearOptions([year, toYear]);
  const fromWeekOpts = weekOptions(year);
  const toWeekOpts = weekOptions(toYear);

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={openEdit}
        className={`w-full text-left rounded-md transition-colors hover:bg-app-hover/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300/60 focus-visible:ring-offset-1 ${viewWrapperClassName}`}
        aria-label={ariaLabel}
      >
        <span
          className={`whitespace-pre-wrap break-words block tabular-nums ${isEmptyDisplay ? 'text-neutral-400 italic' : ''} ${viewTextClassName}`}
        >
          {isEmptyDisplay ? emptyHint : display}
        </span>
      </button>

      <OfferFloatingEditShell
        open={editing}
        onClose={cancel}
        anchorRef={anchorRef}
        panelWidth={340}
        ariaLabel={ariaLabel}
      >
        <div onKeyDown={onKeyDown}>
          <div className="mb-3 flex flex-wrap gap-2">
            <button type="button" className={modeBtn(mode === 'day')} onClick={() => switchToDates('day')}>
              Datum
            </button>
            <button type="button" className={modeBtn(mode === 'range')} onClick={() => switchToDates('range')}>
              Zeitraum
            </button>
            <button type="button" className={modeBtn(mode === 'week')} onClick={switchToWeek}>
              Kalenderwoche
            </button>
          </div>

          {mode === 'week' ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-sm text-app-text">
                  <span className="mb-1.5 block font-medium text-app-text">Jahr</span>
                  <AppSelectDropdown
                    value={String(year)}
                    onChange={(v) => {
                      const nextYear = Number(v);
                      setYear(nextYear);
                      setWeek((w) => clampWeek(nextYear, w));
                    }}
                    options={fromYearOpts}
                    triggerClassName={SELECT_TRIGGER}
                    menuZIndex={UI_Z_INDEX.portalDropdownInModal}
                  />
                </label>
                <label className="block text-sm text-app-text">
                  <span className="mb-1.5 block font-medium text-app-text">KW</span>
                  <AppSelectDropdown
                    value={String(week)}
                    onChange={(v) => setWeek(Number(v))}
                    options={fromWeekOpts}
                    triggerClassName={SELECT_TRIGGER}
                    menuZIndex={UI_Z_INDEX.portalDropdownInModal}
                  />
                </label>
              </div>
              {weekRange ? (
                <div className="grid grid-cols-2 gap-3">
                  <label className="block text-sm text-app-text">
                    <span className="mb-1.5 block font-medium text-app-text">Bis-Jahr</span>
                    <AppSelectDropdown
                      value={String(toYear)}
                      onChange={(v) => {
                        const nextYear = Number(v);
                        setToYear(nextYear);
                        setToWeek((w) => clampWeek(nextYear, w));
                      }}
                      options={toYearOpts}
                      triggerClassName={SELECT_TRIGGER}
                      menuZIndex={UI_Z_INDEX.portalDropdownInModal}
                    />
                  </label>
                  <label className="block text-sm text-app-text">
                    <span className="mb-1.5 block font-medium text-app-text">Bis-KW</span>
                    <AppSelectDropdown
                      value={String(toWeek)}
                      onChange={(v) => setToWeek(Number(v))}
                      options={toWeekOpts}
                      triggerClassName={SELECT_TRIGGER}
                      menuZIndex={UI_Z_INDEX.portalDropdownInModal}
                    />
                  </label>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setToYear(year);
                    setToWeek(week);
                    setWeekRange(true);
                  }}
                  className="text-sm font-medium text-pipedrive-blue hover:underline"
                >
                  Bis-KW (Spanne)
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <label className="block text-sm text-app-text">
                <span className="mb-1.5 block font-medium text-app-text">Von</span>
                <DatePicker
                  value={from}
                  onChange={(v) => {
                    setFrom(v);
                    if (mode === 'range' && to && v > to) setTo(v);
                  }}
                  className="w-full"
                  placeholder="TT.MM.JJJJ"
                />
              </label>
              {mode === 'range' ? (
                <label className="block text-sm text-app-text">
                  <span className="mb-1.5 block font-medium text-app-text">Bis</span>
                  <DatePicker
                    value={to}
                    onChange={(v) => setTo(v)}
                    className="w-full"
                    placeholder="TT.MM.JJJJ"
                  />
                </label>
              ) : null}
            </div>
          )}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-app-border pt-3">
            <div className="flex flex-col items-start gap-1">
              <button
                type="button"
                onClick={() => {
                  if (clearMode === 'reset') {
                    applyParsed(parseOfferLeistungszeitraum(resetValue));
                  } else {
                    setFrom('');
                    setTo('');
                    setMode('day');
                    setWeekRange(false);
                  }
                }}
                disabled={saving}
                className="text-sm font-medium text-app-text-subtle hover:text-rose-700 disabled:opacity-50"
              >
                {clearMode === 'reset' ? 'Auf Rechnungsdatum' : 'Löschen'}
              </button>
              {onHide ? (
                <button
                  type="button"
                  onClick={() => {
                    setEditing(false);
                    void onHide();
                  }}
                  disabled={saving}
                  className="text-sm font-medium text-app-text-subtle hover:text-app-text disabled:opacity-50"
                >
                  Ausblenden
                </button>
              ) : null}
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={cancel} disabled={saving} className={APP_BTN_OUTLINE_CLASS}>
                Abbrechen
              </button>
              <button type="button" onClick={() => void save()} disabled={saving} className={APP_BTN_PRIMARY_CLASS}>
                {saving ? 'Speichern…' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      </OfferFloatingEditShell>
    </>
  );
};
